// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { useLayoutEffect } from "react";
import { defineMessage, useIntl } from "react-intl";
import { GenericError } from "@/ui/errors";
import config from "@/config";
import {
  formatBreadcrumbs,
  useBreadcrumbsFromMatches,
} from "@/utils/breadcrumbs";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  staticData: {
    breadcrumb: {
      message: defineMessage({
        id: "product.title",
        defaultMessage: "Element Admin",
        description: "The main name of the admin console",
      }),
    },
  },
  component: RouteComponent,
  errorComponent: GenericError,
});

function RouteComponent() {
  const intl = useIntl();
  const breadcrumbs = useBreadcrumbsFromMatches();
  const appName = config.appName ?? "Element Admin";

  // Use the original formatBreadcrumbs utility, then replace "Element Admin" with appName
  const rawTitle = formatBreadcrumbs(intl, breadcrumbs);
  const pageTitle = rawTitle.replace("Element Admin", appName);

  useLayoutEffect(() => {
    if (document) {
      document.querySelector("title[data-temp-title]")?.remove();
    }
  }, []);

  return (
    <>
      <title>{pageTitle}</title>
      <Outlet />
    </>
  );
}
